import React, { useEffect, useState } from "react";
import {
  Box,
  Chip,
  Container,
  Grid,
  Paper,
  Typography,
  Button,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, useParams } from "react-router-dom";
import { bplStudentsApi } from "./bplStudentsApi";
import { Student } from "../students/api";

const BPLStudentView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [student, setStudent] = useState<Student | null>(null);

  useEffect(() => {
    if (!id) return;
    (async () => {
      const response = await bplStudentsApi.getById(id);
      setStudent(response as any);
    })();
  }, [id]);

  if (!student) {
    return null;
  }

  const addresses = ((student as any).addresses || []) as any[];
  const primaryAddress = addresses.find((a: any) => a.type === "PRESENT");
  const permanentAddress = addresses.find((a: any) => a.type === "PERMANENT");

  return (
    <Container maxWidth="lg" sx={{ mt: 3, mb: 4 }}>
      <Box
        sx={{
          mb: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/scholar-register/bpl-students")}
            variant="outlined"
            sx={{ textTransform: "none" }}
          >
            Back to BPL List
          </Button>
          <Typography variant="h5">BPL Student Details</Typography>
        </Box>
        <Button
          variant="contained"
          onClick={() =>
            navigate(`/scholar-register/students/${student.id}`)
          }
        >
          Open Full Profile
        </Button>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Student Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography color="textSecondary" variant="subtitle2">
                  Student Name
                </Typography>
                <Typography variant="body1">
                  {student.firstName} {student.middleName} {student.lastName}
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography color="textSecondary" variant="subtitle2">
                  Admission No
                </Typography>
                <Typography variant="body1">
                  {student.admissionNumber}
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography color="textSecondary" variant="subtitle2">
                  Scholar No
                </Typography>
                <Typography variant="body1">
                  {student.scholarNumber || "-"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography color="textSecondary" variant="subtitle2">
                  Class
                </Typography>
                <Typography variant="body1">
                  {student.class?.name || "-"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography color="textSecondary" variant="subtitle2">
                  Section
                </Typography>
                <Typography variant="body1">
                  {student.section?.name || "-"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography color="textSecondary" variant="subtitle2">
                  Admission Date
                </Typography>
                <Typography variant="body1">
                  {student.admissionDate
                    ? new Date(student.admissionDate).toLocaleDateString()
                    : "-"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography color="textSecondary" variant="subtitle2">
                  Status
                </Typography>
                <Chip label={student.status} size="small" color="primary" />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Parent Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography color="textSecondary" variant="subtitle2">
                  Father Name
                </Typography>
                <Typography variant="body1">
                  {student.parent?.fatherName || "-"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography color="textSecondary" variant="subtitle2">
                  Mother Name
                </Typography>
                <Typography variant="body1">
                  {student.parent?.motherName || "-"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography color="textSecondary" variant="subtitle2">
                  Guardian Name
                </Typography>
                <Typography variant="body1">
                  {student.parent?.guardianName || "-"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography color="textSecondary" variant="subtitle2">
                  Mobile
                </Typography>
                <Typography variant="body1">
                  {student.parent?.fatherMobile ||
                    student.parent?.guardianMobile ||
                    student.phone ||
                    "-"}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Address
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography color="textSecondary" variant="subtitle2">
                  Present Address
                </Typography>
                <Typography variant="body1">
                  {primaryAddress
                    ? primaryAddress.addressLine1 ||
                      primaryAddress.addressLine2 ||
                      primaryAddress.city ||
                      primaryAddress.state ||
                      "-"
                    : "-"}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography color="textSecondary" variant="subtitle2">
                  Permanent Address
                </Typography>
                <Typography variant="body1">
                  {permanentAddress
                    ? permanentAddress.addressLine1 ||
                      permanentAddress.addressLine2 ||
                      permanentAddress.city ||
                      permanentAddress.state ||
                      "-"
                    : "-"}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Academic History
            </Typography>
            {student.academicRecords && student.academicRecords.length > 0 ? (
              <Grid container spacing={2}>
                {student.academicRecords.map((record: any) => (
                  <Grid item xs={12} md={4} key={record.id}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="subtitle2">
                        {record.academicSession?.name}
                      </Typography>
                      <Typography variant="body2">
                        {record.class?.name} - {record.section?.name}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Admission:{" "}
                        {record.admissionDate
                          ? new Date(
                              record.admissionDate,
                            ).toLocaleDateString()
                          : "-"}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography variant="body2" color="textSecondary">
                No academic history found.
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default BPLStudentView;
